import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { CSVLink } from 'react-csv';
import translations from '@nike/gameday-translations/web';

import Button from '~COMPONENTS/button/Button';
import Currency from '~COMPONENTS/Currency';
import Date from '../../components/Date';
import NoResults from '../../components/NoResults';
import RenderInBody from '../../components/RenderInBody';
import Spinner from '~COMPONENTS/spinner/Spinner';
import TableClientSide from '~COMPONENTS/table/tableClientSide/TableClientSide';

import SortOptions from '~COMPONENTS/sortOptions/SortOptions';
import SearchFilter from '~COMPONENTS/SearchFilter';
import LoadingMessage from '~COMPONENTS/spinner/LoadingMessage';
import Sidebar from '~COMPONENTS/sidebar/Sidebar';
import DepartmentCard from '~COMPONENTS/card/DepartmentCard';
import PageHeader from '~COMPONENTS/containers/PageHeader';
import Icon from '~COMPONENTS/icon/Icon';

import DepartmentDetails from './sidebar/DepartmentDetails';
import MakeTransfer from './sidebar/MakeTransfer';
import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { hasAuthCheckFinished } from '../../auth/reducer';

import { setSearchTerm } from '../../global/actions';
import { searchTerm } from '../../global/reducer';
import { getUserAccount } from '../../user/selectors';
import { selectShopDepartment } from '../../shop/actions';
import { fetchGamedayAccount } from '../../gamedayAccount/actions';

import { getAllotmentColumns } from './columns';

import {
  mapAllotmentDepts,
  mapAllotmentDeptsBillable,
  mapAndSetAllotmentDeptsBillable
} from '../../util/mapAllotmentDepts';
import { newAllotmentTypes } from '../../types/allotments';
import { userAccountTypes } from '../../types/user';
import { ZERO_DOLLAR_VALUE } from '../../util/constants';
import { accountIsBillable, getGamedayAccount, getIsGreaterChinaSalesOrg } from '../../gamedayAccount/selectors';
import { isEqual } from '../../util/lodash';
import { deptLanguageTranslations, getUserRole } from '../../util/languageTranslations';
import { mapSilhouette } from '~APP_UTIL/silhouettes';
import { GA_EVENT_ALLOTMENTS_MAKE_TRANSFER_CLICK, sendEventToGA } from '../../analytics/constants';

export class DisplayAllotments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      departments: [],
      showListView: !props.isSetup,
      selectedDepartment: null,
      sidebarDetailsOpen: false,
      sidebarTransferOpen: false,
      departmentsLoaded: false,
      selectedBillableDepts: {},
      selectAllChecked: false,
      billableDeptsUpdated: false,
      viewOptionsOpen: false
    };
  }

  componentDidMount() {
    const { allotments, hasAuthCheckFinished } = this.props;
    if (allotments) {
      this.setDepartmentList(mapAllotmentDepts(allotments.departments));
      this.setDepartmentsLoaded(true);
      this.setSelectedBillableDepts(mapAllotmentDeptsBillable(allotments.departments));
    } else if (hasAuthCheckFinished) {
      this.initializeDepartments();
    }
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const { allotments, hasAuthCheckFinished, userAccount, gamedayAccountNumber } = this.props;
    const userAccountChanged = prevProps.userAccount?.number !== userAccount?.number;
    const gamedayAccountChanged = prevProps.gamedayAccountNumber !== gamedayAccountNumber;
    const allotmentDataChanged = !isEqual(prevProps.allotments, allotments);

    if (!prevProps.hasAuthCheckFinished && hasAuthCheckFinished) {
      // Auth check finished on page refresh
      this.initializeDepartments();
    }

    if (userAccountChanged || gamedayAccountChanged || allotmentDataChanged) {
      this.setDepartmentList(mapAllotmentDepts(allotments.departments));
      this.setDepartmentsLoaded(true);
      this.setSelectedBillableDepts(mapAllotmentDeptsBillable(allotments.departments));
      this.setBillableDeptsUpdated(false);
    }

    if (
      userAccount &&
      userAccount.number &&
      prevProps.userAccount &&
      prevProps.userAccount.number &&
      userAccount.number !== prevProps.userAccount.number
    ) {
      this.setSideBarDetailsOpen(false);
      this.setSideBarTransferOpen(false);
      this.clearTransferForm();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = event => {
    const { viewOptionsOpen } = this.state;
    if (this.viewOptionsRef && !this.viewOptionsRef.contains(event.target) && viewOptionsOpen) {
      this.setState({ viewOptionsOpen: !viewOptionsOpen });
    }
  };

  setDepartmentList = departments => this.setState({ departments });

  setSelectedDepartment = department => this.setState({ selectedDepartment: department });

  setSideBarDetailsOpen = bool => this.setState({ sidebarDetailsOpen: bool });

  setSideBarTransferOpen = bool => this.setState({ sidebarTransferOpen: bool });

  setListView = bool => this.setState({ showListView: bool });

  setDepartmentsLoaded = bool => this.setState({ departmentsLoaded: bool });

  setSelectedBillableDepts = selectedBillableDepts => this.setState({ selectedBillableDepts });

  setBillableDeptsUpdated = bool => this.setState({ billableDeptsUpdated: bool });

  setEditDepartment = (dept, isSidebarOpen) => {
    this.setSelectedDepartment(dept);
    this.toggleSidebarDetails(isSidebarOpen);
  };

  mapZeroDollarLockedToNull = allotmentDepts =>
    allotmentDepts.map((dept = {}) => ({
      ...dept,
      lockedAllotment: dept.lockedAllotment === ZERO_DOLLAR_VALUE ? null : dept.lockedAllotment
    }));

  noAllotments = () => {
    const { allotments } = this.props;
    return Object.keys(allotments).length === 0;
  };

  clearTransferForm = () => {
    const { reset } = this.props;
    return reset('allotmentForm');
  };

  toggleView = () => {
    const { showListView } = this.state;
    return this.setListView(!showListView);
  };

  toggleSidebarDetails = bool => this.setSideBarDetailsOpen(bool);

  toggleSidebarTransfer = bool => this.setSideBarTransferOpen(bool);

  initializeDepartments = () => {
    const { allotments, hasAuthCheckFinished } = this.props;
    const { departments } = this.state;
    if (allotments && hasAuthCheckFinished && !departments) {
      this.setDepartmentList(mapAllotmentDepts(allotments.departments));
      this.setDepartmentsLoaded(true);
      this.setSelectedBillableDepts(mapAllotmentDeptsBillable(allotments.departments));
    }
  };

  handleCloseSidebarDetailsClick = () => this.setEditDepartment(null, false);

  handleOpenSidebarTransferClick = () => {
    this.handleCloseSidebarDetailsClick();
    this.setSideBarTransferOpen(true);
  };

  handleCloseSidebarTransferClick = () => {
    const { reset } = this.props;
    reset();
    this.setSideBarTransferOpen(false);
  };

  handleDepartmentClick = deptNum => {
    const { selectedDepartment, departments } = this.state;
    const normalizedNum = deptNum.toString();
    const isSameDept = selectedDepartment && normalizedNum === selectedDepartment.departmentNumber;
    const selectedDept = isSameDept
      ? null
      : departments.filter(({ departmentNumber }) => departmentNumber === normalizedNum)[0];

    this.setEditDepartment(selectedDept, !!selectedDept);
    this.handleCloseSidebarTransferClick();
  };

  handleSortOptionsChange = updatedList => {
    this.setDepartmentList(updatedList); // already mapped to array
  };

  handleCancel = () => {
    const { reset } = this.props;
    this.handleCloseSidebarTransferClick();
    reset();
  };

  handleSubmit = async (updatedList, isTransfer) => {
    const {
      fetchGamedayAccount,
      reset,
      saveAllotments,
      selectShopDepartment,
      setSearchTerm,
      userAccount: { number: accountNumber }
    } = this.props;

    await saveAllotments({ accountNumber }, { departments: updatedList });
    await fetchGamedayAccount({ accountNumber }); // refresh allotments
    if (isTransfer) {
      this.handleCloseSidebarTransferClick();
    } else {
      this.handleCloseSidebarDetailsClick();
    }
    reset();
    setSearchTerm('');
    selectShopDepartment(null);
  };

  handleTransferSubmit = () => {
    const { allotments, allotmentForm, selectShopDepartment } = this.props;
    const { values } = allotmentForm;
    if (!values) return false;
    const transferAmount = parseFloat(values.transferAmt);
    const updatedList = mapAllotmentDepts(allotments.departments).reduce((total, allot) => {
      if (allot.departmentNumber === values.transferFrom.dept.departmentNumber) {
        total.push({ ...allot, allotment: (parseFloat(allot.allotment) - transferAmount).toFixed(2) });
      } else if (allot.departmentNumber === values.transferTo.dept.departmentNumber) {
        total.push({ ...allot, allotment: (parseFloat(allot.allotment) + transferAmount).toFixed(2) });
      }
      return total;
    }, []);
    selectShopDepartment(null);
    return this.handleSubmit(updatedList, true);
  };

  handleDetailsSubmit = e => {
    e.preventDefault();

    const { allotments, allotmentForm } = this.props;
    const { selectedDepartment } = this.state;
    const updatedDepartment = mapAllotmentDepts(allotments.departments)
      .filter(dept => dept.departmentNumber === selectedDepartment.departmentNumber)
      .map(dept => ({
        ...dept,
        billable: allotmentForm.values.deptBillableOrdersCheckbox
      }));

    return this.handleSubmit(updatedDepartment, false);
  };

  handleSelectBillableDept = ({ deptNumber, checked }) => {
    const { selectedBillableDepts } = this.state;
    this.setState({
      selectedBillableDepts: {
        ...selectedBillableDepts,
        [deptNumber]: checked
      }
    });
    this.setBillableDeptsUpdated(true);
  };

  handleSelectAllDeptsBillable = checked => {
    const { allotments } = this.props;
    this.setState({ selectAllChecked: checked });
    const updatedDepartments = mapAndSetAllotmentDeptsBillable(allotments.departments, checked);
    this.setSelectedBillableDepts(updatedDepartments);
    this.setBillableDeptsUpdated(true);
  };

  handleSaveSelectedBillableDepts = e => {
    e.preventDefault();

    const { allotments } = this.props;
    const { selectedBillableDepts } = this.state;
    const updatedDepartments = mapAllotmentDepts(allotments.departments).map(dept => ({
      ...dept,
      billable: selectedBillableDepts[dept.departmentNumber] || false
    }));

    return this.handleSubmit(updatedDepartments, false);
  };

  handleAllotmentSetupClick = e => {
    e.preventDefault();

    const {
      allotments,
      allotmentForm: { values }
    } = this.props;
    const updatedList = mapAllotmentDepts(allotments.departments);
    Object.keys(values).forEach(val => {
      const splitVal = val.split('-');
      const idx = updatedList.findIndex(val => val.departmentNumber === splitVal[0]);
      if (splitVal[1] === 'allotmentAmount') {
        updatedList[idx] = { ...updatedList[idx], allotment: values[val] };
      } else if (splitVal[1] === 'allowBillableOrders') {
        updatedList[idx] = { ...updatedList[idx], billable: values[val] };
      }
    });

    return this.handleSubmit(updatedList, true);
  };

  handleMakeATransferClick = () => {
    this.handleOpenSidebarTransferClick();

    sendEventToGA(GA_EVENT_ALLOTMENTS_MAKE_TRANSFER_CLICK);
  };

  renderButtons = () => {
    const { isSetup, submitting, valid, allotmentForm } = this.props;
    return (
      <>
        {isSetup ? (
          <Button
            extraClassName='ncss-btn-primary-dark full-width'
            disabled={submitting || !valid || (allotmentForm && !allotmentForm.values)}
            type='submit'
            data-desc='allotmentsSetup__button_submit'
            onClick={this.handleAllotmentSetupClick}
          >
            {submitting ? <Spinner className='relative-container' /> : <TranslatedCopy id='allotments.form.save' />}
          </Button>
        ) : (
          <Button
            extraClassName='ncss-btn-primary-dark full-width'
            onClick={this.handleMakeATransferClick}
            data-desc='allotments__button_make-transfer'
            disabled={this.noAllotments()}
          >
            <TranslatedCopy id='allotments.form.makeTransfer' />
          </Button>
        )}
      </>
    );
  };

  mergeLanguages = data => {
    const { en } = translations;

    const translatedTableData = data?.map(item => {
      const manipulatedArrayItem = { ...item };
      const englishVal = en[`shop.deptSelect.deptName.${item.departmentNumber}`];
      if (
        getUserRole() &&
        en[`shop.deptSelect.deptName.${item.departmentNumber}`] &&
        !item.departmentName.includes(englishVal)
      ) {
        manipulatedArrayItem.departmentName = `${item.departmentName} -
        ${en[`shop.deptSelect.deptName.${item.departmentNumber}`]}`;
      }
      return manipulatedArrayItem;
    });
    return translatedTableData;
  };

  clickCSVLink = () => {
    setTimeout(() => this.csvLinkRef?.link?.click(), 1000);
  };

  render() {
    const {
      isAllowedBillable,
      allotmentForm,
      allotments,
      change,
      handleSubmit,
      isGreaterChinaSalesOrg,
      isSetup,
      searchTerm,
      submitting,
      type,
      valid,
      gamedayAccountNumber
    } = this.props;
    const {
      departments,
      departmentsLoaded,
      selectedDepartment,
      sidebarDetailsOpen,
      sidebarTransferOpen,
      showListView,
      selectedBillableDepts,
      selectAllChecked,
      billableDeptsUpdated,
      viewOptionsOpen
    } = this.state;
    const allotmentDeptsList = mapAllotmentDepts(allotments.departments);
    const { en } = translations;

    const translatedAllotmentDeptsList = this.mergeLanguages(allotmentDeptsList);
    const translatedDepartments = this.mergeLanguages(departments);

    const noAllotments = this.noAllotments();

    const allotmentData = this.mapZeroDollarLockedToNull(departments);

    return (
      <form
        className={classnames({
          hasSidebar__main_outerWrapper: sidebarTransferOpen || sidebarDetailsOpen,
          sidebarOpen: sidebarDetailsOpen || sidebarTransferOpen
        })}
        onSubmit={handleSubmit(sidebarDetailsOpen ? this.handleDetailsSubmit : this.handleTransferSubmit)}
      >
        <div className={classnames({ hasSidebar__main_content: sidebarTransferOpen || sidebarDetailsOpen })}>
          <PageHeader
            page='displayAllotments'
            title={
              <span>
                {isSetup ? (
                  <TranslatedCopy id={`allotments.${type}.setup.title`} />
                ) : (
                  <TranslatedCopy id={`allotments.${type}.title`} />
                )}
                {allotments && allotments.contractStartDate && allotments.contractEndDate && (
                  <p className='body-4 lh10-sm text-color-secondary'>
                    <span data-desc='allotments__header_contractStart'>
                      <Date value={allotments.contractStartDate} timeZone='UTC' />
                    </span>
                    {' - '}
                    <span data-desc='allotments__header_contractEnd'>
                      <Date value={allotments.contractEndDate} timeZone='UTC' />
                    </span>
                  </p>
                )}
              </span>
            }
            className='flex-container row bg-white prl10-md'
          >
            <div className='d-sm-h d-md-b'>
              <div className='page-header__buttons flex-container center'>{this.renderButtons()}</div>
            </div>
            <RenderInBody>
              <div className='fixed-buttons d-md-h'>{this.renderButtons()}</div>
            </RenderInBody>
          </PageHeader>
          <div
            className={classnames(
              'hasSidebar__header budget-display bg-white',
              'pt6-sm pb5-sm prl7-sm pt0-md prl10-md pb8-md'
            )}
          >
            {allotments && allotments.totalAllotment && (
              <div>
                <div className='headline-3 u-bold' data-desc={`allotments__${allotments.contractStartDate}_total`}>
                  <Currency
                    value={allotments.totalAllotment}
                    type={allotments.currencyCode}
                    error={allotments.totalAllotment < ZERO_DOLLAR_VALUE}
                  />
                </div>
                <p className='text-color-secondary body-4 lh10-sm'>
                  <TranslatedCopy id='allotments.dashboard.balance' />
                </p>
              </div>
            )}
            {allotments && allotments.unallotedFunds && (
              <div>
                <div
                  className='headline-3 u-bold'
                  data-desc={`allotments__${allotments.contractStartDate}_unallocated`}
                >
                  <Currency
                    value={allotments.unallotedFunds}
                    type={allotments.currencyCode}
                    error={allotments.unallotedFunds < ZERO_DOLLAR_VALUE}
                  />{' '}
                </div>
                <p className='text-color-secondary body-4 lh10-sm'>
                  <TranslatedCopy id='allotments.dashboard.unallocated' />
                </p>
              </div>
            )}
          </div>
          <div
            className={classnames(
              'hasSidebar__main',
              'flex-container row space-between bg-primary-grey pt2-sm pb4-sm prl3-sm prl6-md'
            )}
          >
            {!showListView || isSetup ? (
              <div className='flex-container row space-between u-full-width align-center'>
                <SearchFilter
                  label={new TranslatedCopy({ id: 'allotments.search.label' }).toString()}
                  list={allotments ? translatedAllotmentDeptsList : []}
                  filterBy={['departmentName', 'departmentNumber']}
                  pageName='currentAllotments'
                  setList={this.setDepartmentList}
                />
                <SortOptions
                  list={allotmentData}
                  sortBy={[
                    { value: 'departmentName', name: 'Department Name' },
                    {
                      value: 'remainingAllotment',
                      name: 'Amount Remaining',
                      isNumber: true
                    },
                    {
                      value: 'unitAllotments',
                      name: 'global.unitAllotment'
                    },
                    { value: 'purchaseOptions', name: 'Purchase Option' },
                    { value: 'lockedAllotment', name: 'Locked Allotment', isNumber: true }
                  ]}
                  defaultValue={{ value: 'departmentName', name: 'Department Name' }}
                  toggleView={!isSetup ? this.toggleView : null}
                  handleSortOptionsChange={this.handleSortOptionsChange}
                  currentView='grid'
                />
              </div>
            ) : null}
            {!departmentsLoaded && departments.length === 0 && (
              <LoadingMessage title={new TranslatedCopy({ id: `allotments.${type}.title` }).toString()} />
            )}
            {departmentsLoaded && noAllotments && <NoResults titleId={`allotments.${type}.unavailable`} />}
            {departmentsLoaded && !noAllotments && !searchTerm && departments && departments.length === 0 && (
              <NoResults titleId='allotments.noresults' />
            )}
            {departmentsLoaded && !noAllotments && searchTerm && departments && departments.length === 0 && (
              <NoResults titleId='allotments.search.noresults' />
            )}
            <div
              className={`card-view flex-container row wrap space-between u-full-width ${
                !showListView ? '' : 'hide-view'
              }`}
            >
              {translatedDepartments &&
                translatedDepartments.map(department => (
                  <DepartmentCard
                    department={department}
                    key={`department-${department.departmentNumber}`}
                    onClick={isSetup ? null : e => this.handleDepartmentClick(department.departmentNumber, e)}
                    data-desc={`allotments__card_deparment-${department.departmentNumber}`}
                    isSelected={
                      selectedDepartment && department.departmentNumber === selectedDepartment.departmentNumber
                    }
                    isEditing={isSetup}
                    unallotedFunds={allotments.unallotedFunds}
                    isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
                  />
                ))}
            </div>
            <div className={`table-view u-full-width dept-allotments-table ${showListView ? '' : 'hide-view'}`}>
              {departments && departments.length > 0 && (
                <TableClientSide
                  columns={getAllotmentColumns({
                    isAllowedBillable,
                    handleCheckBillable: this.handleSelectBillableDept,
                    handleCheckAllBillable: this.handleSelectAllDeptsBillable,
                    selectedBillableDepts,
                    selectAllChecked
                  })}
                  data={allotmentData}
                  defaultSort={{ id: 'departmentName', desc: false }}
                  rightHeaderSlot={
                    <>
                      {!isSetup ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {billableDeptsUpdated && (
                            <div style={{ paddingRight: '20px' }}>
                              <Button
                                extraClassName='ncss-btn-primary-dark'
                                onClick={this.handleSaveSelectedBillableDepts}
                              >
                                <TranslatedCopy id='global.saveChanges' />
                              </Button>
                            </div>
                          )}
                          <div className='sortOptions' ref={node => (this.viewOptionsRef = node)}>
                            <button
                              className='controls'
                              onClick={() => this.setState({ viewOptionsOpen: !viewOptionsOpen })}
                              type='button'
                            >
                              <div className='body-3 text-uppercase u-bold'>
                                <TranslatedCopy id='global.viewOptions' />
                              </div>
                            </button>
                            {viewOptionsOpen && (
                              <div
                                style={{
                                  background: 'white',
                                  position: 'absolute',
                                  zIndex: 2,
                                  bottom: 0,
                                  transform: 'translateY(100%)'
                                }}
                              >
                                <div style={{ padding: '15px 20px' }}>
                                  <button
                                    type='button'
                                    onClick={() => this.toggleView('grid')}
                                    title='change to grid view'
                                    style={{ opacity: showListView ? 0.5 : 1 }}
                                  >
                                    <Icon name='view-grid' alt='grid icon' />
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => this.toggleView('list')}
                                    title='change to list view'
                                    style={{ opacity: showListView ? 1 : 0.5, marginLeft: '8px' }}
                                  >
                                    <Icon name='view-list' alt='list icon' />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                      <div style={{ alignContent: 'center', paddingLeft: '20px' }}>
                        <button type='button' onClick={() => this.clickCSVLink()}>
                          <Icon
                            name='download'
                            alt={new TranslatedCopy({ id: 'orderreturns.report.download' }).toString()}
                          />
                        </button>
                        <CSVLink
                          data={
                            allotmentData
                              ?.map(allt => ({
                                departmentName: allt.departmentName,
                                currency: allotments.currencyCode,
                                allocatedAllotment: allt.allotment,
                                remainingBalance: allt.remainingAllotment,
                                unitAllotments: allt.unitAllotments?.length
                                  ? allt.unitAllotments
                                      .map(unitAllt => {
                                        const alltTotalDes = new TranslatedCopy({
                                          id: 'allotments.sidebar.details.unit.details',
                                          values: {
                                            amount: unitAllt.allotmentAmount,
                                            description: mapSilhouette(unitAllt)
                                          }
                                        }).toString();
                                        const alltRemainingDes = new TranslatedCopy({
                                          id: 'allotments.sidebar.details.unit.remaining',
                                          values: {
                                            amount:
                                              parseInt(unitAllt.allotmentAmount, 10) - parseInt(unitAllt.totalSpend, 10)
                                          }
                                        }).toString();
                                        return `${alltTotalDes}\n    ${alltRemainingDes}`;
                                      })
                                      .join('\n')
                                  : '',
                                lockedAllotment: allt.lockedAllotment,
                                allowBillableOrders: allt.billable ? 'Yes' : 'No'
                              }))
                              .sort((a, b) => a.departmentName.localeCompare(b.departmentName)) || []
                          }
                          filename={`${`${gamedayAccountNumber}-${new TranslatedCopy({
                            id: `allotments.${type}.title`
                          })}${allotments ? `-${allotments.contractStartDate}-${allotments.contractEndDate}` : ''}`
                            .toString()
                            .replace(' ', '')}.csv`}
                          ref={node => (this.csvLinkRef = node)}
                          target='_blank'
                        />
                      </div>
                    </>
                  }
                  fullHeight
                />
              )}
            </div>
          </div>
        </div>
        <Sidebar
          isOpen={sidebarDetailsOpen}
          closeSidebar={this.handleCloseSidebarDetailsClick}
          title={
            selectedDepartment &&
            getUserRole() &&
            en[`shop.deptSelect.deptName.${selectedDepartment?.departmentNumber}`]
              ? `${selectedDepartment?.departmentName}-${deptLanguageTranslations(
                  selectedDepartment?.departmentName,
                  selectedDepartment?.departmentNumber
                )}`
              : selectedDepartment?.departmentName
          }
          extraClassName='allotments_details mt4-sm mt0-md'
        >
          {selectedDepartment && (
            <DepartmentDetails
              department={selectedDepartment}
              change={change}
              onSubmit={this.handleDetailsSubmit}
              allotmentForm={allotmentForm}
              submitting={submitting}
              handleSubmit={handleSubmit}
              isAllowedBillable={isAllowedBillable}
              isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
            />
          )}
        </Sidebar>
        <Sidebar
          isOpen={sidebarTransferOpen}
          closeSidebar={this.handleCloseSidebarTransferClick}
          title={<TranslatedCopy id='allotments.transfer.title' />}
          extraClassName='displayAllotments__transfer mt4-sm mt0-md'
        >
          {translatedAllotmentDeptsList.length > 0 && (
            <MakeTransfer
              departments={translatedAllotmentDeptsList}
              unallotedFunds={allotments.unallotedFunds}
              onCancel={this.handleCancel}
              onSubmit={this.handleTransferSubmit}
              isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
              isOpen={sidebarTransferOpen}
              submitting={submitting}
              valid={valid}
              handleSubmit={handleSubmit}
              allotmentForm={allotmentForm}
              currencyCode={allotments.currencyCode}
              type={type}
            />
          )}
        </Sidebar>
      </form>
    );
  }
}

DisplayAllotments.propTypes = {
  isAllowedBillable: PropTypes.bool.isRequired,
  gamedayAccountNumber: PropTypes.string.isRequired,
  allotmentForm: PropTypes.any,
  allotments: newAllotmentTypes,
  userAccount: userAccountTypes,
  type: PropTypes.string.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  isSetup: PropTypes.bool,
  fetchGamedayAccount: PropTypes.func.isRequired,
  saveAllotments: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  selectShopDepartment: PropTypes.func.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  hasAuthCheckFinished: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

DisplayAllotments.defaultProps = {
  allotmentForm: null,
  allotments: { currencyCode: 'USD' }, // will get overwritten
  userAccount: null,
  isSetup: false,
  searchTerm: ''
};

const mapStateToProps = (state, { allotments }) => {
  const gamedayAccount = getGamedayAccount(state);
  const isAllowedBillable = accountIsBillable(state) && !!allotments && !!gamedayAccount.billable?.accountNumber;
  const isGreaterChinaSalesOrg = getIsGreaterChinaSalesOrg(state);

  return {
    gamedayAccountNumber: gamedayAccount.accountNumber,
    isAllowedBillable,
    hasAuthCheckFinished: hasAuthCheckFinished(state),
    isGreaterChinaSalesOrg,
    searchTerm: searchTerm(state),
    userAccount: getUserAccount(state),
    allotmentForm: state.form.allotmentForm
  };
};

const connector = connect(mapStateToProps, {
  selectShopDepartment,
  setSearchTerm,
  fetchGamedayAccount
});

export default connector(DisplayAllotments);
